import {action, computed, makeObservable, observable, reaction} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import OptionField from "../../../forms/fields/OptionField";
import SaporeForm from "../../../forms/SaporeForm";
import FormField from "../../../forms/fields/FormField";
import BooleanField from "../../../forms/fields/BooleanField";
import TextField from "../../../forms/fields/TextField";
import {default as handleErrorToast} from "../../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../../components/toast/SuccessToast";
import {formatDate} from "../../../utils/DateHelper";
import {ORDER_PATH} from "../../../routes";


export default class CreateOrderFeature {
    constructor(application, navigator) {
        this._application = application;
        this._navigator = navigator;
        this._loaded = false;
        this._form = null;
        this._customerOptions = [];
        this._deliverySiteOptions = [];
        this._customerDebtAlert = null;
        this._creationDate = '25/02/2024';
        this._lastEditionDate = '25/02/2024';
        this._enabled_dates = []
        this._ordersWithSamePurchaseOrderNumberAndDeliverySite = []

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            form: computed,
            _form: observable,
            _customerDebtAlert: observable,
            customerDebtAlert: computed,
            _customerOptions: observable,
            customerOptions: computed,
            _deliverySiteOptions: observable,
            deliverySiteOptions: computed,
            _updateOptions: action,
            _enabled_dates: observable,
            isFree: computed,
            setIsFree: action,
            isFormValid: computed,
            _finishLoading: action
        })

        reaction(
            () => this.deliverySiteFieldsAreReady(),
            areDeliverySiteFieldsReady => {
                if (areDeliverySiteFieldsReady) {
                    this._getEnabledDatesForDeliverySite()
                }
            }
        )

        reaction(
            () => this.selectedCustomerBusinessName(),
            customerBusinessName => this._getCustomerDebtAlertOf(customerBusinessName)
        )
    }

    selectedCustomerBusinessName() {
        const customerBusinessName = this.hasCustomerBusinessNameValue() ? this.getFieldByName("businessName").value : null
        const isAChosenCustomer = this._businessNames().includes(customerBusinessName)
        return isAChosenCustomer ? customerBusinessName : null
    }

    get customerDebtAlert() {
        return this._customerDebtAlert
    }

    _getCustomerDebtAlertOf(customerBusinessName) {
        if (!customerBusinessName) {
            this._customerDebtAlert = null
            return
        }
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                const customerIsStillSelected = customerBusinessName === this.selectedCustomerBusinessName()
                if (customerIsStillSelected) {
                    this._customerDebtAlert = response.debtAlert()
                }
            },
            handlesError: (response) => {
                handleErrorToast("No se pudo obtener el estado de deuda del cliente.")
            }
        });
        return this._application.apiClient().customerDebtAlert(customerBusinessName, responseHandler)
    }

    async load() {
        await this.getOptions()
        this._createForm()
        this._finishLoading()
    }

    async createOrder() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._navigator(ORDER_PATH + `/${response.order().number}`)
                handleSuccessToast("El pedido fue creado con éxito.")
            },
            handlesError: (response) => {
                response.errors().map(error => {
                    this.showAlert(error);
                })
            }
        });
        const order = this.orderForm().values;
        return this._application.apiClient().createOrder(order, responseHandler);
    }

    get isFree() {
        return this.getFieldByName("isFree").value;
    }

    setIsFree(isFree) {
        this.getFieldByName("isFree").updateValue(isFree);
        this.deliveryDateField().clear();
        if (this.deliverySiteFieldsAreReady()) {
            this._getEnabledDatesForDeliverySite();
        }
    }

    deliverySiteFieldsAreReady() {
        return this.hasDeliverySiteCodeValue() && this.hasCustomerBusinessNameValue()
    }

    canCreateOrder(){
        return this.hasDeliveryDateCodeValue() && this.deliverySiteFieldsAreReady() && this.hasRequiredObservations()
    }

    hasRequiredObservations() {
        return !this.isFree || this.getFieldByName("observations").hasValue
    }

    getEnabledDatesForDeliverySiteWithPurchaseOrderNumber() {
        if (this.deliverySiteFieldsAreReady()){
            this.deliveryDateField().clear()
            this._getEnabledDatesForDeliverySite()
        }
    }

    async clearFormFields(){
        this.form.clear();
        await this.updateFormOptions()
    }

    async clearDeliverySite() {
        this.orderForm().getFieldByName("deliverySiteName").clear()
        this.orderForm().getFieldByName("deliverySiteAddress").clear()
        this.orderForm().getFieldByName("deliveryDate").clear()
        this.orderForm().getFieldByName("deliverySiteCity").clear()
        this.orderForm().getFieldByName("deliverySiteCode").clear()
        await this.updateFormOptions()
    }

    showAlert(error) {
        const errorCode = error.code
        const data = error.data

        if (errorCode === 'duplicated_editable_order') {
            handleErrorToast(`Ya existe un Pedido en estado ${this.stateTextFrom(data.state)} para el Punto de Entrega ${data.delivery_site_name}` +
                ` con fecha de entrega ${data.delivery_date}. El mismo fue creado el día ${data.creation_date} por el usuario` +
                ` ${data.creator_name}. Podes editarlo desde el listado de pedidos.`)

        } else if (errorCode === 'duplicated_non_editable_order') {
            handleErrorToast(`Ya existe una Nota de Pedido en estado ${this.stateTextFrom(data.state)} para el Punto de Entrega ${data.delivery_site_name}` +
                ` con fecha de entrega ${data.delivery_date}. El mismo fue creado el día ${data.creation_date} por el usuario` +
                ` ${data.creator_name}. El mismo ya no puede modificarse.`)

        } else if (errorCode === 'incorrect_delivery_date_for_delivery_site') {
            handleErrorToast("No se puede entregar este pedido para esa fecha.")
        } else {
            handleErrorToast("No se pudo crear el pedido.")
        }
    }

    stateTextFrom(state) {
        return state === 'INITIAL' ? "Abierto" :
            state === 'PENDING' ? "Cerrado" :
                state === 'CONFIRMED' ? "Proceso de Facturación" :
                    state === 'INVOICED' ? "Facturada" :
                        state === 'REVISION' ? 'En Revisión' :
                            state === 'REJECTED' ? 'Rechazado' :
                                "Desconocido"

    }

    isDateValidForDelivery(aDateTime) {
        return this._enabled_dates.some(date => {
            const enabledDate = formatDate(date)
            const calendarDate = formatDate(new Date(aDateTime))
            return enabledDate === calendarDate
        });
    }

    async getOptions() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._updateOptions(response.customerOptions(), response.deliverySiteOptions());
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener los clientes")
            }
        });
        // TODO: reify partialOrder when order edition has to be implemented. Now the order form has all the order data,
        // we have to separate the customer and the delivery site in two forms
        let partialOrder;
        if (this.orderForm()) {
            partialOrder = this.orderForm().values;
        } else {
            partialOrder = {
                "businessName": '',
                "fantasyName": '',
                "customerCode": '',
                "deliverySiteName": '',
                "deliverySiteAddress": '',
                "deliverySiteCity": '',
            }
        }
        return this._application.apiClient().getOrderOptions(partialOrder, responseHandler)
    }

    _updateOptions(customerOptions, deliverySiteOptions) {
        this._customerOptions = customerOptions;
        this._deliverySiteOptions = deliverySiteOptions;
    }

    _getEnabledDatesForDeliverySite() {
        const deliverySiteCode = this.orderForm().getFieldByName("deliverySiteCode").value
        const customerBusinessName = this.orderForm().getFieldByName("businessName").value
        const purchaseOrderNumber = this.orderForm().getFieldByName("purchaseOrderNumber").value
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._enabled_dates = response.responseEnabledDates();
                handleSuccessToast("Se obtuvieron las fechas habilitadas para el punto de entrega.")
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener las fechas habilitadas para el punto de entrega.")
            }
        });
        if (this.isFree) {
            return this._application.apiClient().getEnabledDatesForFreeOrderDeliverySite(deliverySiteCode, customerBusinessName, responseHandler)
        }
        const orderNumber = null
        return this._application.apiClient().getEnabledDatesForDeliverySite(deliverySiteCode, customerBusinessName, purchaseOrderNumber, orderNumber, responseHandler)
    }

    async ordersForSamePurchaseOrderNumberAndDeliverySite() {
        const deliverySiteCode = this.orderForm().getFieldByName("deliverySiteCode").value
        const customerBusinessName = this.orderForm().getFieldByName("businessName").value
        const purchaseOrderNumber = this.orderForm().getFieldByName("purchaseOrderNumber").value
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._ordersWithSamePurchaseOrderNumberAndDeliverySite = response.orders();
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener otros pedidos con la misma fecha y numero de orden de compra")
            }
        });

        return this._application.apiClient().ordersForSamePurchaseOrderNumberAndDeliverySite(deliverySiteCode, customerBusinessName, purchaseOrderNumber, responseHandler)
    }

    hasOrdersWithSamePurchaseOrderNumberAndDeliverySite(){
        return (this._ordersWithSamePurchaseOrderNumberAndDeliverySite.length > 0)
    }

    ordersWithSamePurchaseOrderNumberAndDeliverySite(){
        return this._ordersWithSamePurchaseOrderNumberAndDeliverySite
    }

    deliverySiteNameField(){
        return this.orderForm().getFieldByName("deliverySiteName").value
    }

    purchaseOrderNumberField(){
        return this.orderForm().getFieldByName("purchaseOrderNumber").value
    }

    _createForm() {
        const businessNames = this._businessNames()
        const customerCodes = this._customerCodes()
        const fantasyNames = this._fantasyNames()
        const deliverySiteNames = this._deliverySiteNames()
        const deliverySiteAddresses = this._deliverySiteAddresses()
        const deliverySiteCities = this._deliverySiteCities()
        const deliverySiteCodes = this._deliverySiteCodes()
        const fields = {
            "businessName": new OptionField(businessNames, true, this),
            "purchaseOrderNumber": new TextField(2, 20, true),
            "customerCode": new OptionField(customerCodes, true, this),
            "fantasyName": new OptionField(fantasyNames, true, this),
            "deliverySiteName": new OptionField(deliverySiteNames, true, this),
            "deliverySiteAddress": new OptionField(deliverySiteAddresses, true, this),
            "deliverySiteCity": new OptionField(deliverySiteCities, true, this),
            "deliverySiteCode": new OptionField(deliverySiteCodes, true, this),
            "deliveryDate": new FormField(true),
            "isFree": new BooleanField(false),
            "observations": new TextField(0, 1000, true, '', this),
        }
        this.form = new SaporeForm(fields);
    }

    async updateFormOptions() {
        await this.getOptions()
        this.updateOptionFields()
    }

    updateOptionFields() {
        const fields = this.orderForm().fields()
        fields["businessName"].updateOptions(this._businessNames())
        fields["customerCode"].updateOptions(this._customerCodes())
        fields["fantasyName"].updateOptions(this._fantasyNames())
        fields["deliverySiteName"].updateOptions(this._deliverySiteNames())
        fields["deliverySiteAddress"].updateOptions(this._deliverySiteAddresses())
        fields["deliverySiteCity"].updateOptions(this._deliverySiteCities())
        fields["deliverySiteCode"].updateOptions(this._deliverySiteCodes())
    }

    orderForm() {
        return this.form;
    }

    _getCustomerFieldValues(customerField) {
        return this._customerOptions[customerField]
    }

    _getDeliverySiteFieldValues(deliverySiteField) {
        return this._deliverySiteOptions[deliverySiteField]
    }

    _deliverySiteCities() {
        return this._getDeliverySiteFieldValues("deliverySiteCity");
    }

    _deliverySiteCodes() {
        return this._getDeliverySiteFieldValues("deliverySiteCode");
    }

    deliverySiteCityOptions() {
        return this.optionsFor(this._deliverySiteCities());
    }

    _deliverySiteAddresses() {
        return this._getDeliverySiteFieldValues("deliverySiteAddress");
    }

    deliverySiteAddressOptions() {
        return this.optionsFor(this._deliverySiteAddresses());
    }

    _deliverySiteNames() {
        return this._getDeliverySiteFieldValues("deliverySiteName");
    }

    deliverySiteNameOptions() {
        return this.optionsFor(this._deliverySiteNames());
    }

    _fantasyNames() {
        return this._getCustomerFieldValues("fantasyName");
    }

    fantasyNameOptions() {
        return this.optionsFor(this._fantasyNames());
    }

    _customerCodes() {
        return this._getCustomerFieldValues("code");
    }

    customerCodeOptions() {
        return this.optionsFor(this._customerCodes());
    }

    _businessNames() {
        return this._getCustomerFieldValues("businessName");
    }

    businessNameOptions() {
        return this.optionsFor(this._businessNames());
    }

    optionsFor(values) {
        return values.map(value => {
            return {label: value, value: value}
        });
    }

    get isFormValid() {
        return this.orderForm().isValid;
    }

    get form() {
        return this._form;
    }

    set form(newForm) {
        this._form = newForm
    }

    creationDate() {
        return this._creationDate
    }

    lastEditionDate() {
        return this._lastEditionDate
    }

    get loaded() {
        return this._loaded;
    }

    _finishLoading() {
        this._loaded = true;
    }

    get customerOptions() {
        return this._customerOptions
    }

    get deliverySiteOptions() {
        return this._deliverySiteOptions
    }

    deliveryDateField() {
        return this.getFieldByName("deliveryDate");
    }

    getFieldByName(fieldName) {
        return this.orderForm().getFieldByName(fieldName);
    }

    hasDeliverySiteCodeValue() {
        return this.orderForm() && this.orderForm().getFieldByName("deliverySiteCode").hasValue
    }

    hasDeliveryDateCodeValue() {
        return this.orderForm() && this.orderForm().getFieldByName("deliveryDate").hasValue
    }

    hasCustomerBusinessNameValue() {
        return this.orderForm() && this.orderForm().getFieldByName("businessName").hasValue
    }
}